import { Fragment, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Pencil, Plus, Users, Trash2, Landmark } from "lucide-react";
import api from "../../lib/api";
import Avatar from "../Avatar";
import { fmtR } from "./useScorecard";
import { AddMembersModal, BudgetAllModal, BudgetModal, EntryModal, GroupEntryModal, LoanModal } from "./ScorecardModals";

export const GroupTag = ({ e }) => (e.group_id
  ? <span className="ml-1 text-[9px] uppercase px-1 py-px rounded border border-cyan-400/40 text-cyan-200" title={`Group entry · ${e.group_size || ""} people`} data-testid={`scorecard-group-tag-${e.id}`}>Group</span>
  : null);
import { toast } from "../ui/sonner";
import { useAuth } from "../../context/AuthContext";

const iconBtn = "inline-flex items-center justify-center w-5 h-5 rounded hover:bg-white/10 text-white/50 hover:text-white";

function EntryList({ member, canEdit, onChanged }) {
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get(`/scorecard/members/${member.member_id}/entries`).then(({ data }) => setRows(data.items || [])).catch(() => setRows([]));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [member.member_id, member.entry_count, member.updated_at]);
  return (
    <div className="pl-10 pr-2 py-2 space-y-1 bg-white/[0.02]" data-testid={`scorecard-entries-${member.member_id}`}>
      {rows.length === 0 && <div className="text-xs text-white/40">No penalties or rewards yet.</div>}
      {rows.map((e) => (
        <div key={e.id} className={`flex items-center gap-3 text-xs ${e.voided_at ? "opacity-40 line-through" : ""}`} data-testid={`scorecard-entry-${e.id}`}>
          <span className="w-20 text-white/50 font-mono">{e.entry_date}</span>
          <span className={`w-16 text-[10px] uppercase font-semibold ${e.kind === "penalty" ? "text-rose-300" : "text-emerald-300"}`}>{e.kind}</span>
          <span className={`w-20 font-mono ${e.kind === "penalty" ? "text-rose-200" : "text-emerald-200"}`}>{e.kind === "penalty" ? "−" : "+"}{fmtR(e.amount)}</span>
          <span className="flex-1 text-white/80">{e.reason}<GroupTag e={e} />{(e.edit_log || []).length ? <span className="text-white/30"> · edited</span> : null}</span>
          <span className="text-white/40 hidden md:inline">{e.created_by_name}</span>
          {canEdit && <button className={iconBtn} onClick={() => setEdit(e)} data-testid={`scorecard-entry-edit-${e.id}`}><Pencil className="w-3 h-3" /></button>}
        </div>
      ))}
      {edit && <EntryModal entry={edit} member={member} onClose={() => setEdit(null)} onDone={() => { load(); onChanged(); }} />}
    </div>
  );
}

function MemberRow({ m, project, canEdit, canLoan, highlight, onChanged }) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const remove = async () => {
    if (!window.confirm(`Remove ${m.first_name} from this project's scorecard? Their history is kept.`)) return;
    try { await api.post(`/scorecard/members/${m.member_id}/remove`, {}); toast.success("Removed"); onChanged(); } catch { toast.error("Could not remove."); }
  };
  const closed = project.status !== "active";
  return (
    <Fragment>
      <tr className={`border-t border-white/5 cursor-pointer hover:bg-white/[0.03] ${highlight ? "bg-amber-400/10" : ""}`}
        onClick={() => setOpen((o) => !o)} data-testid={`scorecard-row-${m.member_id}`}>
        <td className="py-2 pl-2">
          <div className="flex items-center gap-2">
            {open ? <ChevronDown className="w-3.5 h-3.5 text-white/40" /> : <ChevronRight className="w-3.5 h-3.5 text-white/40" />}
            <span className="w-5 text-[11px] text-white/40 font-mono">{m.rank}</span>
            <Avatar user={{ name: m.name, email: m.email, avatar_url: m.avatar_url }} size="sm" />
            <span className="text-sm text-white">{m.first_name}</span>
          </div>
        </td>
        {[["budget", m.budget, "text-white/80"], ["penalties", m.penalties, "text-rose-300"], ["rewards", m.rewards, "text-emerald-300"]].map(([k, v, c]) => (
          <td key={k} className={`py-2 text-right font-mono text-sm ${c}`}>
            <span className="inline-flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
              {k === "penalties" && v ? "−" : ""}{fmtR(v)}
              {canEdit && k === "budget" && !closed && <button className={iconBtn} onClick={() => setModal("budget")} data-testid={`scorecard-budget-edit-${m.member_id}`}><Pencil className="w-3 h-3" /></button>}
              {canEdit && k !== "budget" && !closed && (
                <button className={iconBtn} onClick={() => setModal(k === "penalties" ? "penalty" : "reward")} data-testid={`scorecard-${k === "penalties" ? "penalty" : "reward"}-add-${m.member_id}`}><Plus className="w-3 h-3" /></button>
              )}
            </span>
          </td>
        ))}
        <td className={`py-2 pr-2 text-right font-mono text-sm font-semibold ${m.balance < 0 ? "text-rose-400" : "text-cyan-200"}`} data-testid={`scorecard-balance-${m.member_id}`}>
          <span className="inline-flex items-center gap-1 justify-end" onClick={(e) => e.stopPropagation()}>
            {fmtR(m.balance)}
            {m.loan_id && <Link to={`/team/${m.user_id}?tab=loans`} title="Staff loan created" className="text-amber-300"><Landmark className="w-3.5 h-3.5" /></Link>}
            {canEdit && canLoan && closed && m.balance < 0 && !m.loan_id && (
              <button className="text-[10px] px-1.5 py-0.5 rounded border border-amber-400/50 text-amber-200" onClick={() => setModal("loan")} data-testid={`scorecard-create-loan-${m.member_id}`}>Create loan</button>
            )}
            {canEdit && <button className={iconBtn} onClick={remove} title="Remove from scorecard" data-testid={`scorecard-remove-${m.member_id}`}><Trash2 className="w-3 h-3" /></button>}
          </span>
        </td>
      </tr>
      {open && <tr><td colSpan={5}><EntryList member={m} canEdit={canEdit} onChanged={onChanged} /></td></tr>}
      {modal === "budget" && <BudgetModal member={m} onClose={() => setModal(null)} onDone={onChanged} />}
      {(modal === "penalty" || modal === "reward") && <EntryModal member={m} kind={modal} onClose={() => setModal(null)} onDone={onChanged} />}
      {modal === "loan" && <LoanModal member={m} projectName={project.project_name} onClose={() => setModal(null)} onDone={onChanged} />}
    </Fragment>
  );
}

const LOAN_ROLES = ["owner", "admin"];

export function ProjectBoard({ project, canEdit, highlightIds = [], onChanged, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  const [modal, setModal] = useState(null);
  const { user } = useAuth();
  const groupable = canEdit && project.status === "active" && project.members.length > 0;
  return (
    <div className="rounded-lg border border-white/10 overflow-hidden" data-testid={`scorecard-project-${project.project_id}`}>
      <div className="flex items-center gap-2 px-3 py-2 bg-white/[0.03]">
        <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 flex-1 text-left">
          {open ? <ChevronDown className="w-4 h-4 text-white/50" /> : <ChevronRight className="w-4 h-4 text-white/50" />}
          <Link to={`/projects/${project.project_id}`} onClick={(e) => e.stopPropagation()} className="text-sm font-semibold text-white hover:underline">
            {project.short_id ? `${project.short_id} · ` : ""}{project.project_name}
          </Link>
          <span className="text-[11px] text-white/40">{project.members.length} members · balance {fmtR(project.total_balance)}</span>
        </button>
        {canEdit && project.status === "active" && (
          <>
            <button className="hv-btn-ghost text-[11px] inline-flex items-center gap-1" onClick={() => setModal("members")} data-testid={`scorecard-add-members-${project.project_id}`}><Users className="w-3 h-3" /> Add members</button>
            <button className="hv-btn-ghost text-[11px]" onClick={() => setModal("budget")} data-testid={`scorecard-budget-all-${project.project_id}`}>Budget for all</button>
          </>
        )}
      </div>
      {open && (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px]">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-white/40">
                <th className="text-left py-1.5 pl-2 font-medium">Member</th>
                <th className="text-right py-1.5 font-medium">Budget</th>
                {[["penalty", "Penalties"], ["reward", "Rewards"]].map(([k, l]) => (
                  <th key={k} className="text-right py-1.5 font-medium">
                    <span className="inline-flex items-center gap-1 justify-end">
                      {l}
                      {groupable && (
                        <button className={iconBtn} title={`Group ${k} — all members`} onClick={() => setModal(`group-${k}`)} data-testid={`scorecard-group-${k}-add-${project.project_id}`}><Plus className="w-3 h-3" /></button>
                      )}
                    </span>
                  </th>
                ))}
                <th className="text-right py-1.5 pr-2 font-medium">Balance</th>
              </tr>
            </thead>
            <tbody>
              {project.members.map((m) => (
                <MemberRow key={m.member_id} m={m} project={project} canEdit={canEdit} canLoan={LOAN_ROLES.includes(user?.role)} highlight={highlightIds.includes(m.member_id)} onChanged={onChanged} />
              ))}
              {project.members.length === 0 && <tr><td colSpan={5} className="text-xs text-white/40 p-3">No members yet{canEdit ? " — use Add members." : "."}</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {modal === "members" && <AddMembersModal projectId={project.project_id} defaultBudget={project.default_budget} members={project.members} onClose={() => setModal(null)} onDone={onChanged} />}
      {modal === "budget" && <BudgetAllModal project={project} onClose={() => setModal(null)} onDone={onChanged} />}
      {modal?.startsWith("group-") && <GroupEntryModal project={project} kind={modal.slice(6)} onClose={() => setModal(null)} onDone={onChanged} />}
    </div>
  );
}

export default function ScorecardBoard({ projects, canEdit, highlightIds, onChanged, emptyText = "No active projects on the scorecard yet." }) {
  if (!projects?.length) return <div className="text-xs text-white/50 p-3" data-testid="scorecard-empty">{emptyText}</div>;
  return (
    <div className="space-y-3" data-testid="scorecard-board">
      {projects.map((p) => <ProjectBoard key={p.project_id} project={p} canEdit={canEdit} highlightIds={highlightIds} onChanged={onChanged} />)}
    </div>
  );
}
